import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SAPI, config, errorText } from '../ServicerFields';

const running = job => ['queued', 'running'].includes(job?.status);

export const useImportConfirmation = (operation, onChange) => {
  const [job, setJob] = useState(null), [submitting, setSubmitting] = useState(false);
  const [connection, setConnection] = useState(''), [uncertain, setUncertain] = useState(false);
  const changed = useRef(onChange), notified = useRef(null);
  const mounted = useRef(true), currentOid = useRef(operation.id);
  currentOid.current = operation.id;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  changed.current = onChange;
  const oid = operation.id;
  const endpoint = `${SAPI}/operaciones/${oid}/confirmacion`;
  const accept = useCallback(async value => {
    if (!mounted.current || currentOid.current !== oid) return value;
    setJob(value); setConnection(''); setUncertain(false);
    if (value && !running(value) && notified.current !== `${value.id}:${value.status}`) {
      const op = (await axios.get(`${SAPI}/operaciones/${oid}`, { ...config, timeout: 15000 })).data;
      if (!mounted.current || currentOid.current !== oid) return value;
      changed.current(op);
      notified.current = `${value.id}:${value.status}`;
    }
    return value;
  }, [oid]);

  useEffect(() => {
    let active = true, timer;
    const read = async () => {
      try {
        const value = (await axios.get(endpoint, { ...config, timeout: 15000 })).data;
        if (!active) return;
        await accept(value);
        if (active && running(value)) timer = setTimeout(read, 1500);
      } catch {
        if (!active) return;
        setConnection('Reconectando con el progreso. No vuelvas a confirmar: el trabajo puede seguir ejecutándose.');
        timer = setTimeout(read, 3000);
      }
    };
    read();
    return () => { active = false; clearTimeout(timer); };
  }, [endpoint, accept, job?.id, job?.status, uncertain]);

  const start = async body => {
    setSubmitting(true); setConnection('');
    try {
      await accept((await axios.post(`${SAPI}/operaciones/${oid}/confirmar`, body, { ...config, timeout: 15000 })).data);
    } catch (e) {
      if (e.response?.status && e.response.status < 500) {
        toast.error(errorText(e));
        return;
      }
      // Una pérdida de respuesta no significa que el servidor haya rechazado el trabajo.
      try {
        const saved = (await axios.get(endpoint, { ...config, timeout: 15000 })).data;
        if (saved && saved.revision === body.revision && saved.solo_datos === !!body.solo_datos) await accept(saved);
        else setConnection('No se ha encontrado una confirmación registrada. Puedes volver a confirmar.');
      } catch {
        setUncertain(true);
        setConnection('Comprobando si la confirmación quedó registrada. El resultado se recuperará al reconectar.');
      }
    } finally { setSubmitting(false); }
  };
  const resume = async () => {
    setSubmitting(true);
    try { await accept((await axios.post(`${endpoint}/reanudar`, {}, { ...config, timeout: 15000 })).data); }
    catch (e) { toast.error(errorText(e)); }
    finally { setSubmitting(false); }
  };
  return { job, connection, start, resume, submitting, active: (job?.operation_id === oid && running(job)) || submitting || uncertain };
};